import { useState, useCallback, useEffect } from 'react';
import { saveItemRequest, removeItemRequest } from '@/requests/savedItemsApiRequest';
// Default cookie name for saved items
// This can be overridden by passing a different cookie name to the hook
const DEFAULT_COOKIE_NAME = 'saved_items';
export const useSavedItems = (cookieName, api) => {
    const [savedItems, setSavedItems] = useState([]);
    const [lastError, setLastError] = useState(null);
    const apiUrl = api?.endsWith('/') ? api.slice(0, -1) : api; // Ensure no trailing slash
    const cookie = cookieName || DEFAULT_COOKIE_NAME;
    // Reusable function to load saved items from cookies
    const loadSavedItemsFromCookie = useCallback(() => {
        try {
            const cookieValue = document.cookie
                .split('; ')
                .find((row) => row.startsWith(`${cookie}=`))
                ?.split('=')[1];
            if (cookieValue) {
                const decodedValue = decodeURIComponent(cookieValue);
                const backendResponse = JSON.parse(decodedValue);
                // Transform backend format to frontend format
                const items = backendResponse.Items.map((item) => ({
                    contentId: item.ContentId,
                    contentTypeId: item.ContentTypeId,
                    savedDate: item.SavedDate,
                }));
                setSavedItems(items);
                return items;
            }
            else {
                setSavedItems([]);
                return [];
            }
        }
        catch (error) {
            setSavedItems([]);
            return [];
        }
    }, [cookieName]);
    // Load saved items from cookies on mount
    useEffect(() => {
        loadSavedItemsFromCookie();
    }, [loadSavedItemsFromCookie]);
    const hasCookieConsent = useCallback(() => {
        if (window.OnetrustActiveGroups) {
            try {
                // Check if functional cookies group (C0003) is active
                // C0003 Functional Cookies enable a website to provide enhanced functionality and personalization
                return window.OnetrustActiveGroups.includes('C0003');
            }
            catch (error) {
                console.warn('Failed to check OneTrust consent:', error);
            }
        }
        return false;
    }, []);
    // Show cookie consent prompt
    const showCookieConsentPrompt = useCallback(() => {
        // Trigger OneTrust cookie consent modal
        if (window.OneTrust) {
            window.OneTrust.ToggleInfoDisplay();
        }
    }, []);
    // API functions for server-side saved items
    const saveItemToServer = useCallback(async (contentId, contentTypeId) => {
        return await saveItemRequest(apiUrl, contentId, contentTypeId);
    }, [apiUrl]);
    const removeItemFromServer = useCallback(async (contentId) => {
        return await removeItemRequest(apiUrl, contentId);
    }, [apiUrl]);
    const clearError = useCallback(() => {
        setLastError(null);
    }, []);
    const isSaved = useCallback((contentId) => {
        return savedItems.some((item) => item.contentId === contentId);
    }, [savedItems]);
    const toggleItem = useCallback(async (contentId, contentTypeId) => {
        // Clear any previous errors
        setLastError(null);
        if (window.OneTrust && !hasCookieConsent()) {
            showCookieConsentPrompt();
            return { success: false, error: 'Cookie consent required' };
        }
        const isCurrentlySaved = savedItems.some((item) => item.contentId === contentId);
        // Optimistically update local state (UI only)
        setSavedItems((prev) => {
            if (isCurrentlySaved) {
                // Remove item if it's currently saved
                return prev.filter((item) => item.contentId !== contentId);
            }
            else {
                // Add item if it's not currently saved
                return [...prev, { contentId, contentTypeId, savedDate: new Date().toISOString() }];
            }
        });
        // Update server and get the authoritative state
        try {
            let updatedItems;
            if (isCurrentlySaved) {
                updatedItems = await removeItemFromServer(contentId);
            }
            else {
                updatedItems = await saveItemToServer(contentId, contentTypeId);
            }
            setSavedItems(updatedItems);
            return { success: true };
        }
        catch (error) {
            // Extract error message from server response
            let errorMessage = '';
            if (error instanceof Error) {
                try {
                    const errorData = JSON.parse(error.message);
                    errorMessage = errorData.error || error.message;
                }
                catch (parseError) {
                    errorMessage = error.message;
                }
            }
            // Set the error for the hook to expose
            setLastError(errorMessage);
            // If server update fails, revert local state
            setSavedItems((prev) => {
                return isCurrentlySaved
                    ? [...prev, { contentId, contentTypeId, savedDate: new Date().toISOString() }]
                    : prev.filter((item) => item.contentId !== contentId);
            });
            return { success: false, error: errorMessage };
        }
    }, [
        savedItems,
        hasCookieConsent,
        showCookieConsentPrompt,
        saveItemToServer,
        removeItemFromServer,
        loadSavedItemsFromCookie,
    ]);
    return {
        savedItems,
        isSaved,
        toggleItem,
        lastError,
        clearError,
    };
};
